<template>
  <div class="md-layout product-images-page">
    <div class="md-layout-item">

      <md-card class="images-card">
        <md-card-header class="images-header" :class="isAccessories ? 'header-accessories' : 'header-standard'">
          <div class="header-icon-wrap">
            <md-icon>{{ isAccessories ? 'palette' : 'photo_library' }}</md-icon>
          </div>
          <div class="header-text">
            <h4 class="title">{{ isAccessories ? 'Colour Variants & Images' : 'Product Images' }}</h4>
            <p class="subtitle" v-if="produtDetail">{{ produtDetail.displayName || produtDetail.name }}</p>
          </div>
        </md-card-header>

        <md-card-content class="images-body">

          <!-- ═══════════════ ACCESSORIES MODE ═══════════════ -->
          <template v-if="isAccessories">

            <!-- Add colour variant form -->
            <div class="add-variant-bar">
              <h5 class="bar-title">Add Colour Variant</h5>
              <div class="variant-form-row">
                <div class="form-group">
                  <label class="field-label">Colour Name <span class="required">*</span></label>
                  <input v-model="newVariant.color" class="field-input" placeholder="e.g. Midnight Black" />
                </div>
                <div class="form-group color-picker-group">
                  <label class="field-label">Colour Swatch</label>
                  <div class="color-picker-row">
                    <input v-model="newVariant.colorCode" type="color" class="color-dot-picker" />
                    <input v-model="newVariant.colorCode" class="field-input" placeholder="#000000" style="flex:1" />
                  </div>
                </div>
                <div class="form-group checkbox-group">
                  <label class="field-label">Default</label>
                  <label class="toggle-switch">
                    <input type="checkbox" v-model="newVariant.isDefault" />
                    <span class="toggle-slider"></span>
                  </label>
                </div>
                <div class="form-group btn-group-right">
                  <label class="field-label">&nbsp;</label>
                  <button class="add-variant-btn" @click="addColorVariant" :disabled="!newVariant.color.trim() || addingVariant">
                    <i v-if="addingVariant" class="fa fa-spinner fa-spin"></i>
                    <md-icon v-else>add</md-icon>
                    Add Variant
                  </button>
                </div>
              </div>
            </div>

            <!-- Variant tabs -->
            <div v-if="colorVariants.length > 0">
              <div class="variant-tabs">
                <button
                  v-for="(v, i) in colorVariants"
                  :key="v._id"
                  class="variant-tab"
                  :class="{ active: activeVariantIndex === i }"
                  @click="activeVariantIndex = i"
                >
                  <span class="tab-swatch" :style="{ background: v.colorCode || '#ccc' }"></span>
                  {{ v.color }}
                  <span v-if="v.isDefault" class="default-badge">Default</span>
                  <button class="delete-variant-tab-btn" @click.stop="deleteColorVariant(v._id)" title="Delete variant">
                    <md-icon>close</md-icon>
                  </button>
                </button>
              </div>

              <div class="variant-panel" v-if="activeVariant">
                <div class="variant-panel-header">
                  <div class="variant-color-preview" :style="{ background: activeVariant.colorCode || '#ccc' }"></div>
                  <div>
                    <h5 class="variant-name">{{ activeVariant.color }}</h5>
                    <small class="variant-code">{{ activeVariant.colorCode || 'No colour code' }}</small>
                  </div>
                  <label class="default-toggle-row">
                    <span>Set as default</span>
                    <label class="toggle-switch">
                      <input type="checkbox" :checked="activeVariant.isDefault" @change="setVariantDefault(activeVariant._id, $event.target.checked)" />
                      <span class="toggle-slider"></span>
                    </label>
                  </label>
                </div>

                <!-- Variant images -->
                <div class="variant-images-grid">
                  <div
                    v-for="(img, ii) in activeVariant.images"
                    :key="ii"
                    class="image-tile"
                  >
                    <img :src="buildImageUrl(img)" class="tile-img" @click="openPreview(buildImageUrl(img))" />
                    <button class="tile-delete-btn" @click="deleteVariantImage(activeVariant._id, img)" title="Remove image">
                      <md-icon>close</md-icon>
                    </button>
                  </div>

                  <!-- Upload tile -->
                  <div
                    class="image-tile upload-tile"
                    @click="triggerVariantUpload(activeVariant._id)"
                    @dragover.prevent
                    @drop.prevent="(e) => handleVariantDrop(e, activeVariant._id)"
                  >
                    <md-icon class="upload-icon">add_photo_alternate</md-icon>
                    <span>Add Image</span>
                    <input
                      :ref="`variantInput_${activeVariant._id}`"
                      type="file" multiple accept="image/*"
                      @change="(e) => handleVariantFileChange(e, activeVariant._id)"
                      style="display:none"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div v-else class="empty-variants">
              <md-icon>palette</md-icon>
              <p>No colour variants yet</p>
              <small>Add your first colour variant above.</small>
            </div>

          </template>

          <!-- ═══════════════ STANDARD MODE ═══════════════ -->
          <template v-else>

            <!-- Image Gallery -->
            <div v-if="productImages.length > 0" class="standard-gallery">
              <div
                v-for="(img, i) in productImages"
                :key="i"
                class="image-tile"
              >
                <img :src="img.productImage" class="tile-img" @click="openPreview(img.productImage)" />
                <button class="tile-delete-btn" @click="deleteImage(img._id)" title="Remove image">
                  <md-icon>close</md-icon>
                </button>
              </div>
            </div>

            <div v-else class="empty-images">
              <md-icon>photo_library</md-icon>
              <p>No images uploaded yet</p>
            </div>

            <!-- Upload Area -->
            <div
              class="upload-drop-area"
              @click="$refs.fileInput.click()"
              @dragover.prevent
              @drop.prevent="handleStandardDrop"
            >
              <md-icon class="upload-cloud">cloud_upload</md-icon>
              <p>Click or drag & drop to upload images</p>
              <small>PNG, JPG, WEBP — multiple files supported</small>
              <input ref="fileInput" type="file" multiple accept="image/*" @change="handleStandardFileChange" style="display:none" />
            </div>

          </template>

        </md-card-content>
      </md-card>

    </div>

    <!-- ── Full-screen preview ── -->
    <transition name="fade">
      <div v-if="showPreview" class="image-modal" @click.self="closePreview">
        <div class="image-modal-content">
          <button class="close-btn" @click="closePreview"><md-icon>close</md-icon></button>
          <img :src="selectedImage" class="preview-image" />
        </div>
      </div>
    </transition>

  </div>
</template>

<script>
import OrdersDataService from '../../services/SuperAdmin/Kids/OrdersDataService';

export default {
  data() {
    return {
      productdId: this.$route.query.id,
      produtDetail: null,
      productImages: [],       // standard mode
      colorVariants: [],       // accessories mode
      activeVariantIndex: 0,
      showPreview: false,
      selectedImage: null,
      addingVariant: false,
      newVariant: { color: '', colorCode: '#000000', isDefault: false },
    };
  },

  computed: {
    isAccessories() {
      return this.produtDetail?.productType === 'Accessories';
    },
    activeVariant() {
      return this.colorVariants[this.activeVariantIndex] || null;
    },
  },

  methods: {
    // ── Data loading ──────────────────────────────────────────────
    loadData() {
      OrdersDataService.getWingioProductImages({ id: this.productdId })
        .then((r) => {
          this.produtDetail = r.data.produtDetail;
          if (this.isAccessories) {
            this.colorVariants = this.produtDetail.colorVariants || [];
          } else {
            this.productImages = r.data.data;
          }
        })
        .catch((e) => {
          this.$toast.error(e.response?.data?.respones_msg || 'Failed to load data');
        });
    },

    buildImageUrl(filename) {
      if (filename.startsWith('http')) return filename;
      return `${process.env.VUE_APP_API_BASE_URL || ''}images/${filename}`;
    },

    // ── Standard images ───────────────────────────────────────────
    handleStandardFileChange(e) {
      this.uploadStandardImages(Array.from(e.target.files));
      e.target.value = '';
    },
    handleStandardDrop(e) {
      this.uploadStandardImages(Array.from(e.dataTransfer.files).filter((f) => f.type.startsWith('image/')));
    },
    uploadStandardImages(files) {
      if (!files.length) return;
      const fd = new FormData();
      fd.append('productId', this.productdId);
      files.forEach((f) => fd.append('images[]', f));
      OrdersDataService.uploadWingioProductImages(fd)
        .then(() => {
          this.$toast.success('Images uploaded successfully!');
          this.loadData();
        })
        .catch((e) => this.$toast.error(e.response?.data?.respones_msg || 'Upload failed'));
    },
    deleteImage(id) {
      OrdersDataService.deleteWingioProductImages({ id })
        .then((r) => {
          this.$toast.success(r.data.respones_msg);
          this.loadData();
        })
        .catch((e) => this.$toast.error(e.response?.data?.respones_msg || 'Delete failed'));
    },

    // ── Colour variants ───────────────────────────────────────────
    addColorVariant() {
      if (!this.newVariant.color.trim()) return;
      this.addingVariant = true;
      const data = { productId: this.productdId, ...this.newVariant };
      OrdersDataService.addColorVariant(data)
        .then((r) => {
          this.$toast.success('Colour variant added!');
          this.colorVariants = r.data.data;
          this.activeVariantIndex = this.colorVariants.length - 1;
          this.newVariant = { color: '', colorCode: '#000000', isDefault: false };
        })
        .catch((e) => this.$toast.error(e.response?.data?.respones_msg || 'Failed to add variant'))
        .finally(() => { this.addingVariant = false; });
    },
    deleteColorVariant(variantId) {
      OrdersDataService.deleteColorVariant({ productId: this.productdId, variantId })
        .then(() => {
          this.$toast.success('Variant deleted.');
          this.colorVariants = this.colorVariants.filter((v) => v._id !== variantId);
          if (this.activeVariantIndex >= this.colorVariants.length) {
            this.activeVariantIndex = Math.max(0, this.colorVariants.length - 1);
          }
        })
        .catch((e) => this.$toast.error(e.response?.data?.respones_msg || 'Delete failed'));
    },
    setVariantDefault(variantId, isDefault) {
      OrdersDataService.updateColorVariant({ productId: this.productdId, variantId, isDefault })
        .then((r) => {
          this.$toast.success('Default variant updated.');
          this.colorVariants = r.data.data;
        })
        .catch((e) => this.$toast.error(e.response?.data?.respones_msg || 'Update failed'));
    },

    // ── Variant image upload ──────────────────────────────────────
    triggerVariantUpload(variantId) {
      const ref = this.$refs[`variantInput_${variantId}`];
      if (ref) (Array.isArray(ref) ? ref[0] : ref).click();
    },
    handleVariantFileChange(e, variantId) {
      this.uploadVariantImages(Array.from(e.target.files), variantId);
      e.target.value = '';
    },
    handleVariantDrop(e, variantId) {
      this.uploadVariantImages(
        Array.from(e.dataTransfer.files).filter((f) => f.type.startsWith('image/')),
        variantId
      );
    },
    uploadVariantImages(files, variantId) {
      if (!files.length) return;
      const fd = new FormData();
      fd.append('productId', this.productdId);
      fd.append('colorVariantId', variantId);
      files.forEach((f) => fd.append('images[]', f));
      OrdersDataService.uploadWingioProductImages(fd)
        .then(() => {
          this.$toast.success('Images uploaded!');
          this.loadData();
        })
        .catch((e) => this.$toast.error(e.response?.data?.respones_msg || 'Upload failed'));
    },
    deleteVariantImage(variantId, filename) {
      OrdersDataService.deleteColorVariantImage({ productId: this.productdId, colorVariantId: variantId, filename })
        .then(() => {
          this.$toast.success('Image removed.');
          this.loadData();
        })
        .catch((e) => this.$toast.error(e.response?.data?.respones_msg || 'Delete failed'));
    },

    // ── Preview ───────────────────────────────────────────────────
    openPreview(src) {
      this.selectedImage = src;
      this.showPreview = true;
      document.addEventListener('keydown', this.handleEsc);
    },
    closePreview() {
      this.showPreview = false;
      this.selectedImage = null;
      document.removeEventListener('keydown', this.handleEsc);
    },
    handleEsc(e) {
      if (e.key === 'Escape') this.closePreview();
    },
  },

  beforeMount() {
    this.loadData();
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.handleEsc);
  },
};
</script>

<style scoped>
.product-images-page { font-family: 'Segoe UI', sans-serif; }
.images-card { border-radius: 14px; overflow: hidden; box-shadow: 0 4px 20px rgba(0,0,0,0.08); }

/* Header */
.images-header {
  display: flex; align-items: center; gap: 16px;
  padding: 22px 28px; color: white;
}
.header-standard { background: linear-gradient(135deg, #1976d2, #0d47a1); }
.header-accessories { background: linear-gradient(135deg, #8e24aa, #4a148c); }
.header-icon-wrap {
  width: 48px; height: 48px; border-radius: 12px;
  background: rgba(255,255,255,0.2);
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.header-icon-wrap .md-icon { color: white !important; font-size: 24px !important; }
.header-text { flex: 1; }
.images-header .title { color: white; margin: 0; font-size: 18px; font-weight: 600; }
.images-header .subtitle { color: rgba(255,255,255,0.75); margin: 2px 0 0; font-size: 13px; }

.images-body { padding: 0; }

/* ── Add variant bar ── */
.add-variant-bar {
  padding: 20px 28px;
  background: #fafafa;
  border-bottom: 1px solid #efefef;
}
.bar-title { font-size: 14px; font-weight: 600; color: #555; margin: 0 0 14px; }
.variant-form-row { display: flex; gap: 16px; align-items: flex-start; flex-wrap: wrap; }
.variant-form-row .form-group { margin: 0; }
.variant-form-row .form-group:first-child { flex: 2; min-width: 180px; }
.color-picker-group { flex: 1.5; min-width: 160px; }
.color-picker-row { display: flex; align-items: center; gap: 8px; }
.color-dot-picker {
  width: 40px; height: 38px; border: 1.5px solid #e0e0e0; border-radius: 8px;
  padding: 2px; cursor: pointer; background: white;
}
.checkbox-group { display: flex; flex-direction: column; }
.btn-group-right { display: flex; flex-direction: column; }
.field-label { display: block; font-size: 11px; font-weight: 700; color: #666; margin-bottom: 5px; text-transform: uppercase; letter-spacing: 0.4px; }
.required { color: #e53935; }
.field-input {
  width: 100%; padding: 8px 11px; border: 1.5px solid #e0e0e0;
  border-radius: 8px; font-size: 14px; color: #333;
  outline: none; transition: border-color 0.2s; box-sizing: border-box; background: white;
}
.field-input:focus { border-color: #8e24aa; }

/* Toggle switch */
.toggle-switch { position: relative; display: inline-block; width: 42px; height: 24px; cursor: pointer; }
.toggle-switch input { opacity: 0; width: 0; height: 0; }
.toggle-slider {
  position: absolute; inset: 0;
  background: #ccc; border-radius: 24px; transition: 0.2s;
}
.toggle-slider::before {
  content: ''; position: absolute;
  width: 18px; height: 18px; border-radius: 50%;
  background: white; left: 3px; top: 3px; transition: 0.2s;
}
.toggle-switch input:checked + .toggle-slider { background: #8e24aa; }
.toggle-switch input:checked + .toggle-slider::before { transform: translateX(18px); }

/* Add variant button */
.add-variant-btn {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 8px 16px; border-radius: 8px; border: none;
  background: #8e24aa; color: white; font-size: 13px; font-weight: 600;
  cursor: pointer; transition: background 0.15s; white-space: nowrap;
}
.add-variant-btn:hover:not(:disabled) { background: #6a1b9a; }
.add-variant-btn:disabled { opacity: 0.6; cursor: not-allowed; }
.add-variant-btn .md-icon { color: white !important; font-size: 17px !important; }

/* Variant tabs */
.variant-tabs {
  display: flex; gap: 0; overflow-x: auto;
  padding: 0 28px;
  border-bottom: 2px solid #f0f0f0;
  background: white;
}
.variant-tab {
  display: flex; align-items: center; gap: 7px;
  padding: 13px 18px; border: none; background: transparent;
  cursor: pointer; font-size: 13px; font-weight: 500; color: #888;
  border-bottom: 3px solid transparent; margin-bottom: -2px;
  white-space: nowrap; transition: all 0.15s; position: relative;
}
.variant-tab:hover { color: #8e24aa; background: #fdf3ff; }
.variant-tab.active { color: #8e24aa; border-bottom-color: #8e24aa; font-weight: 600; }
.tab-swatch { width: 14px; height: 14px; border-radius: 50%; border: 1.5px solid rgba(0,0,0,0.12); flex-shrink: 0; }
.default-badge {
  background: #f3e5f5; color: #7b1fa2;
  font-size: 10px; font-weight: 700; padding: 1px 7px;
  border-radius: 10px; letter-spacing: 0.3px; text-transform: uppercase;
}
.delete-variant-tab-btn {
  background: none; border: none; cursor: pointer; padding: 2px 2px 0;
  margin-left: 2px; border-radius: 4px; line-height: 1;
  opacity: 0.4; transition: opacity 0.15s;
}
.variant-tab:hover .delete-variant-tab-btn { opacity: 0.8; }
.delete-variant-tab-btn .md-icon { font-size: 14px !important; color: #888 !important; }

/* Variant panel */
.variant-panel { padding: 24px 28px; }
.variant-panel-header {
  display: flex; align-items: center; gap: 14px;
  margin-bottom: 20px; padding-bottom: 16px;
  border-bottom: 1px solid #f0f0f0;
}
.variant-color-preview {
  width: 40px; height: 40px; border-radius: 10px;
  border: 2px solid rgba(0,0,0,0.1); flex-shrink: 0;
}
.variant-name { font-size: 16px; font-weight: 600; color: #333; margin: 0; }
.variant-code { color: #888; font-size: 12px; }
.default-toggle-row {
  display: flex; align-items: center; gap: 10px; margin-left: auto;
  font-size: 13px; color: #555;
}

/* Image grid */
.variant-images-grid, .standard-gallery {
  display: flex; flex-wrap: wrap; gap: 12px;
}
.image-tile {
  width: 130px; height: 130px; border-radius: 10px;
  border: 1.5px solid #e8e8e8; overflow: hidden;
  position: relative; background: #fafafa;
  transition: box-shadow 0.15s;
}
.image-tile:hover { box-shadow: 0 4px 12px rgba(0,0,0,0.12); }
.tile-img { width: 100%; height: 100%; object-fit: cover; cursor: pointer; display: block; }
.tile-delete-btn {
  position: absolute; top: 5px; right: 5px;
  background: rgba(0,0,0,0.5); border: none; border-radius: 50%;
  width: 24px; height: 24px; display: flex; align-items: center; justify-content: center;
  cursor: pointer; opacity: 0; transition: opacity 0.15s;
}
.image-tile:hover .tile-delete-btn { opacity: 1; }
.tile-delete-btn .md-icon { color: white !important; font-size: 14px !important; }
.upload-tile {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 4px; cursor: pointer; border-style: dashed; border-color: #ccc;
  color: #aaa; font-size: 12px; transition: all 0.15s;
}
.upload-tile:hover { border-color: #8e24aa; color: #8e24aa; background: #fdf3ff; }
.upload-icon { font-size: 26px !important; color: inherit !important; }

/* Standard upload drop area */
.standard-gallery { padding: 24px 28px 16px; }
.upload-drop-area {
  margin: 0 28px 24px;
  border: 2px dashed #ccc; border-radius: 12px;
  padding: 32px 20px; text-align: center; cursor: pointer;
  color: #aaa; transition: all 0.2s;
}
.upload-drop-area:hover { border-color: #1976d2; color: #1976d2; background: #f0f7ff; }
.upload-cloud { font-size: 40px !important; color: inherit !important; display: block; margin-bottom: 8px; }
.upload-drop-area p { margin: 0 0 4px; font-size: 14px; }
.upload-drop-area small { font-size: 12px; }

/* Empty states */
.empty-variants, .empty-images {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  padding: 60px 20px; color: #ccc; text-align: center;
}
.empty-variants .md-icon, .empty-images .md-icon { font-size: 48px !important; color: #ddd !important; margin-bottom: 12px; display: block; }
.empty-variants p, .empty-images p { margin: 0 0 4px; font-size: 15px; color: #aaa; }
.empty-variants small, .empty-images small { font-size: 13px; }
.empty-images { padding: 40px 28px 0; }

/* Modal */
.fade-enter-active, .fade-leave-active { transition: opacity 0.2s; }
.fade-enter, .fade-leave-to { opacity: 0; }
.image-modal {
  position: fixed; inset: 0;
  background: rgba(0,0,0,0.92);
  display: flex; align-items: center; justify-content: center;
  z-index: 9999; padding: 20px;
}
.image-modal-content { position: relative; max-width: 90vw; max-height: 90vh; }
.preview-image { max-width: 100%; max-height: 85vh; border-radius: 8px; display: block; }
.close-btn {
  position: absolute; top: -14px; right: -14px;
  background: rgba(255,255,255,0.15); border: none; border-radius: 50%;
  width: 36px; height: 36px; display: flex; align-items: center; justify-content: center;
  cursor: pointer; transition: background 0.15s;
}
.close-btn:hover { background: rgba(255,255,255,0.25); }
.close-btn .md-icon { color: white !important; font-size: 18px !important; }
</style>